import { Icon } from '@eventbrite/eds-icon';
import { setWindowLocation } from '@eventbrite/http';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import classNames from 'classnames';
import React, { PureComponent } from 'react';
import { Link } from 'react-router-dom';
import { BucketHeader } from '../../../../components/BucketHeader';
import { SeeMoreButton } from '../../../../components/SeeMoreButton';
import {
    CATEGORY_NAME_ICON_MAP,
    CATEGORY_TEMPLATE,
    EVENTBRITE_CATEGORY_TO_REFINEMENT,
} from '../../../../constants';
import {
    GA_CITY_BROWSE_CATEGORY,
    GA_VIEW_CATEGORY_ACTION,
    GA_VIEW_MORE_ACTION,
} from '../../../../constants/analytics';
import { FeatureFlags } from '../../../../types';
import './categoryBucket.scss';

const Category = ({
    name,
    url,
    isCarousel,
    iconType,
    trackClick,
    enableClientSideLinking,
}: {
    url: string;
    name?: string;
    isCarousel?: boolean;
    iconType: React.ReactNode;
    trackClick?: Function;
    enableClientSideLinking?: boolean;
}) => {
    if (!url) {
        return null;
    }

    const classes = classNames('eds-link--neutral', 'category-item', {
        'category-item--mobile': isCarousel,
        'eds-g-cell': !isCarousel,
        'eds-g-cell-2-12': !isCarousel,
    });

    const content = (
        <>
            <div className="eds-align--center">
                <Icon type={iconType} size="medium" hasHalo={true} />
            </div>
            <div className="eds-text--center eds-l-pad-top-4">{name}</div>
        </>
    );

    return enableClientSideLinking ? (
        <span className={classes}>
            <Link to={url} onClick={trackClick?.bind(null, name)}>
                {content}
            </Link>
        </span>
    ) : (
        <span className={classes}>
            <a href={url} onClick={trackClick?.bind(null, name)}>
                {content}
            </a>
        </span>
    );
};

const _getRenderedCategories = (
    categories: { url?: string; name?: string; id?: number }[],
    isCarousel?: boolean,
    trackClick?: Function,
    enableClientSideLinking?: boolean,
    locationSlug?: string,
) =>
    categories.map((category) => {
        const { url = '', name, id } = category;
        const iconType = CATEGORY_NAME_ICON_MAP[id || 0];

        let linkUrl = url;

        if (enableClientSideLinking) {
            const categoryId = `EventbriteCategory/${id}`;
            const categoryRefinment =
                EVENTBRITE_CATEGORY_TO_REFINEMENT[categoryId];
            linkUrl = `/b/${locationSlug}/${categoryRefinment}/`;
        }

        if (!iconType) {
            return null;
        }

        return (
            <Category
                name={name}
                url={linkUrl}
                key={id}
                iconType={iconType}
                isCarousel={isCarousel}
                trackClick={trackClick}
                enableClientSideLinking={enableClientSideLinking}
            />
        );
    });

const CategoryGroup = ({
    categories,
    trackClick,
    trackSeeMoreClick,
    seeMoreText,
    seeMoreUrl,
    title,
    enableClientSideLinking,
    locationSlug,
}: {
    categories: { url: string; name: string; id: number }[];
    trackClick?: Function;
    trackSeeMoreClick?: Function;
    seeMoreText?: string;
    seeMoreUrl: string;
    title: string | GenericLazyString;
    enableClientSideLinking?: boolean;
    locationSlug?: string;
}) => {
    const isCarousel = categories.length > 6;

    const classes = classNames('eds-show-up-md', {
        'mobile-card-carousel': isCarousel,
        'eds-g-group': !isCarousel,
    });

    return (
        <div className="eds-l-mar-top-6 eds-l-mn-mar-top-10 eds-l-md-mar-top-10 eds-l-mw-mar-top-10 eds-l-ln-mar-top-10 eds-l-lg-mar-top-10 eds-l-lw-mar-top-10">
            <div className={classes}>
                {_getRenderedCategories(
                    categories,
                    isCarousel,
                    trackClick,
                    enableClientSideLinking,
                    locationSlug,
                )}
            </div>
            <div className="mobile-card-carousel eds-show-down-mn">
                {_getRenderedCategories(
                    categories,
                    true,
                    trackClick,
                    enableClientSideLinking,
                    locationSlug,
                )}
            </div>
            <div className="eds-l-pad-top-6">
                <div className="eds-show-down-sn eds-align--center">
                    <SeeMoreButton
                        title={title}
                        linkUrl={seeMoreUrl}
                        trackClick={
                            trackSeeMoreClick ||
                            (() => {
                                return;
                            })
                        }
                        seeMoreText={seeMoreText}
                        gaAction={GA_VIEW_MORE_ACTION}
                    />
                </div>
            </div>
        </div>
    );
};

interface CategoryBucketProps {
    /*
     * Array of preformatted objects that will represent that entities within the bucket
     */
    content: {
        id: number;
        name: string;
        url: string;
    }[];
    /*
     * The name of the given bucket ie. "Upcoming Music Events"
     */
    name: string;
    /*
     * Url for all events directory page
     */
    allEventsUrl?: string;
    /*
     * Track GA Events from state
     */
    trackGAEvent?: Function;
    /*
     * Track GA Events from state on PageUnload
     */
    trackGAEventOnPageUnload?: Function;
    /**
     * Feature flags object
     */
    featureFlags?: FeatureFlags;
    /**
     * url location slug
     */
    locationSlug?: string;
}

export class CategoryBucket extends PureComponent<CategoryBucketProps> {
    _handleTrackClick(
        name: CategoryBucketProps['name'],
        trackGAEvent: CategoryBucketProps['trackGAEvent'],
    ) {
        trackGAEvent?.({
            category: GA_CITY_BROWSE_CATEGORY,
            action: GA_VIEW_CATEGORY_ACTION,
            label: name,
        });
    }

    _trackSeeMoreClick = (action: string, url: string) => {
        const { name, trackGAEventOnPageUnload } = this.props;

        if (name) {
            trackGAEventOnPageUnload?.({
                category: GA_CITY_BROWSE_CATEGORY,
                action,
                label: name,
            });
        }

        /* Need to fire tracking action on the seemore, so necessary to
        manually set windowlocation rather that relying on the link. */
        if (url) {
            setWindowLocation(url);
        }
    };

    render() {
        const {
            content,
            name,
            allEventsUrl,
            trackGAEvent,
            featureFlags,
            locationSlug,
        } = this.props;
        const seeMoreText = gettext('Search all events').toString();

        const enableClientSideLinking = featureFlags?.indexCategoryBrowse;

        return (
            <div>
                <BucketHeader
                    title={name}
                    linkUrl={allEventsUrl}
                    seeMoreText={seeMoreText}
                    template={CATEGORY_TEMPLATE}
                    trackGAEvent={trackGAEvent}
                />
                <CategoryGroup
                    categories={content}
                    trackClick={this._handleTrackClick.bind(this)}
                    trackSeeMoreClick={this._trackSeeMoreClick.bind(this)}
                    title={name}
                    seeMoreUrl={allEventsUrl || ''}
                    seeMoreText={seeMoreText}
                    enableClientSideLinking={enableClientSideLinking}
                    locationSlug={locationSlug}
                />
            </div>
        );
    }
}
